import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Lock, LogOut, LayoutDashboard, Plus, User, Settings } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="app-layout">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <Lock className="w-4 h-4 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <span className="font-serif font-bold text-xl tracking-tight text-foreground">{t('common.app_name')}</span>
          </Link>

          <nav className="flex items-center gap-1">
            <Link
              to="/dashboard"
              data-testid="nav-dashboard"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/dashboard') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="hidden sm:inline">{t('dashboard.title')}</span>
            </Link>
            <Link
              to="/agreements/create"
              data-testid="nav-create-agreement"
              className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm transition-colors ${
                isActive('/agreements/create') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('dashboard.create_agreement')}</span>
            </Link>
            <div className="w-px h-6 bg-border mx-2" />
            <Link
              to="/settings"
              data-testid="nav-settings"
              className={`p-2 rounded-sm transition-colors ${
                isActive('/settings') ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              aria-label={t('settings.title')}
            >
              <Settings className="w-4 h-4" />
            </Link>
            <div className="flex items-center gap-2 px-2">
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <span className="text-sm font-medium hidden md:inline">{user?.name}</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 text-muted-foreground hover:text-foreground rounded-sm hover:bg-muted transition-colors"
              aria-label={t('auth.logout')}
            >
              <LogOut className="w-4 h-4" />
            </button>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-6 md:px-12 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 px-6 md:px-12">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground tracking-wider uppercase">{t('common.app_name')}</span>
          </div>
          <p className="text-xs text-muted-foreground/60 text-center max-w-xl" data-testid="legal-disclaimer">
            {t('disclaimer.text')}
          </p>
        </div>
      </footer>

      <Toaster position="top-right" />
    </div>
  );
}
